import { HANDLER_STATUSES } from "queryClient";
import createHeaders from "api/createHeaders";
import createBody from "api/createBody";
import { CONTENT_TYPE } from "constants/headers";

/**
 * @callback CreateMethod
 * @param {RequestInfo} endpoint
 * @param {RequestInit} [options]
 * @param {'json' | 'blob' | 'text' | 'document'} [responseParse=json]
 */

class FetchError extends Error {
  constructor(response, message) {
    super(message);
    this.response = response;
  }
}

/**
 * @description Manual `AbortController` + `setTimeout` rather than
 * `AbortSignal.timeout` - this repo's browserslist (`not dead`, production)
 * doesn't guarantee that static is present everywhere, and the fallback
 * costs nothing. The timer is cleared once the request settles either way,
 * so it never fires (and never aborts) a request that already finished.
 */
function timeoutController(timeoutMs) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  return { signal: controller.signal, clear: () => clearTimeout(timer) };
}

async function parseResponse(response, responseParse) {
  if (HANDLER_STATUSES.includes(`${response.status}`)) {
    const parsedResponse = await response.json();
    // `.message` is now populated on this branch too. It used to be left
    // undefined (so `error.message` was the empty string) because every 401/403
    // unconditionally ended in `logout()` — nothing was ever going to display
    // it. That is no longer true: `queryClient.js` lets an authorization 403 on
    // a prospect-list query/mutation through to the caller to render, and an
    // empty toast is not a message. `.response` is unchanged.
    throw new FetchError(parsedResponse, parsedResponse.message);
  }

  if (response.ok) return response[responseParse]?.();

  // These two don't attempt `response.json()` — a gateway/proxy error page
  // is not guaranteed to be JSON — so `.response` is built by hand instead
  // of parsed. `.message` is unchanged from before; `.response.statusCode`
  // is new, for the same reason as the generic branch below.
  if (response.status === 502) {
    throw new FetchError({ statusCode: 502, message: "502 Bad Gateway" }, "502 Bad Gateway");
  }

  if (response.status === 504) {
    throw new FetchError(
      { statusCode: 504, message: "504 Gateway Timeout" },
      "504 Gateway Timeout",
    );
  }

  const parsedResponse = await response.json();
  // `.message` mirrors the old plain-Error behavior (existing callers that
  // read `error.message` are unaffected); `.response` is new here and lets
  // callers that need it (e.g. distinguishing 403 vs 404) branch on
  // `error.response.statusCode`, same as the 401/403 case above already did.
  throw new FetchError(parsedResponse, parsedResponse.message);
}

/**
 * @type {CreateMethod}
 * */
async function client(
  endpoint,
  { body, method, headers: initHeaders, timeoutMs, ...rest },
  responseParse = "json",
) {
  const headers = createHeaders(initHeaders);

  // Opt-in only: omitting `timeoutMs` (the default for every existing
  // caller) leaves `signal` exactly as `rest` already had it - nothing
  // changes for callers that don't pass it. When it IS passed (and the
  // caller hasn't supplied its own `signal` - an explicit one always wins),
  // this bounds how long a single request can hang; without it a stalled
  // connection waits on the browser's own TCP timeout, which can be
  // minutes or effectively unbounded.
  const timeout = timeoutMs && !rest.signal ? timeoutController(timeoutMs) : null;

  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/${endpoint}`, {
      method,
      headers,
      body: createBody({ body, contentType: headers.get(CONTENT_TYPE) }),
      ...rest,
      signal: timeout?.signal ?? rest.signal,
    });

    return await parseResponse(response, responseParse);
  } finally {
    timeout?.clear();
  }
}

/**
 * @param {'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'} method
 *
 * @return CreateMethod
 * */
const createMethod = (method) => (url, options, responseParse) =>
  client(url, { method, ...options }, responseParse);

export default {
  get: createMethod("GET"),
  post: createMethod("POST"),
  put: createMethod("PUT"),
  patch: createMethod("PATCH"),
  delete: createMethod("DELETE"),
};
